import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertCircle,
  Bookmark,
  Briefcase,
  CheckCircle2,
  ChevronRight,
  DollarSign,
  FileText,
  Lightbulb,
  MapPin,
  Search,
  Sparkles,
  Timer,
  X,
  Zap,
} from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useFeaturePreviewCopy } from './useFeaturePreviewCopy';
import { useHeroPreviewCopy } from './useHeroPreviewCopy';
import { useAgenticPreviewJobs } from './usePreviewJobFixtures';

const TYPING_MS = 30;

type SimPhase = 'typing' | 'searching' | 'matches' | 'cursor' | 'drawer';

type PreviewJob = {
  id: string;
  title: string;
  company: string;
  location: string;
  score: number;
  skills: string[];
  source: string;
  jobType: string;
  experienceLevel: string;
  salary: string;
  postedLabel: string;
  description: string;
  matchedSkills: string[];
  missingSkills: string[];
  bonusSkills: string[];
  whyMatched: string;
  actionItems: string[];
  saved?: boolean;
};

function PreviewMatchRing({
  score,
  ringId,
  size = 'sm',
  highlight = false,
}: {
  score: number;
  ringId: string;
  size?: 'sm' | 'md';
  highlight?: boolean;
}) {
  const dim = size === 'md' ? 44 : 36;
  const radius = size === 'md' ? 17 : 14;
  const center = dim / 2;
  const circumference = 2 * Math.PI * radius;
  const dash = (Math.max(0, Math.min(100, score)) / 100) * circumference;
  const gradientId = `hfs-preview-ring-${ringId.replace(/[^a-zA-Z0-9_-]/g, '')}`;

  return (
    <div
      className={`home-hero-preview-match-ring relative shrink-0 ${size === 'md' ? 'h-11 w-11' : 'h-9 w-9'} ${highlight ? 'hfs-agent-preview__ring--highlight' : ''}`}
      aria-label={`${score} percent match`}
    >
      <svg width={dim} height={dim} viewBox={`0 0 ${dim} ${dim}`} className="block" aria-hidden>
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#22d3ee" />
            <stop offset="50%" stopColor="#6C47FF" />
            <stop offset="100%" stopColor="#7c3aed" />
          </linearGradient>
        </defs>
        <circle cx={center} cy={center} r={radius} fill="none" stroke="rgba(148, 163, 184, 0.22)" strokeWidth="2.5" />
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={`url(#${gradientId})`}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circumference}`}
          transform={`rotate(-90 ${center} ${center})`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
        <span className={`font-bold uppercase tracking-wide text-slate-400 ${size === 'md' ? 'text-[5px]' : 'text-[4px]'}`}>
          Match
        </span>
        <span className={`font-black text-gray-950 dark:text-white ${size === 'md' ? 'text-[9px]' : 'text-[8px]'}`}>
          {score}%
        </span>
      </div>
    </div>
  );
}

function PreviewJobCard({
  job,
  selected,
  highlightRing,
  cardRef,
}: {
  job: PreviewJob;
  selected?: boolean;
  highlightRing?: boolean;
  cardRef?: React.Ref<HTMLButtonElement>;
}) {
  const heroCopy = useHeroPreviewCopy();
  const initial = job.company.charAt(0).toUpperCase();
  const visibleSkills = job.skills.slice(0, 3);
  const extraSkills = Math.max(0, job.skills.length - 3);

  return (
    <button
      ref={cardRef}
      type="button"
      className={`home-hero-preview-job-card home-hero-preview-job-card--portrait home-hero-preview-job-card--with-ring flex h-full w-full min-h-0 flex-col text-left ${
        selected ? 'home-hero-preview-job-card--selected' : ''
      }`}
    >
      <div className="absolute right-1 top-1 z-[1]">
        <PreviewMatchRing score={job.score} ringId={job.id} highlight={highlightRing} />
      </div>

      <div className="relative shrink-0">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-50 text-[11px] font-bold text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-300">
          {initial}
        </div>
      </div>

      <div className="mt-1.5 min-w-0 shrink-0 pr-9">
        <p className="line-clamp-2 text-[11px] font-bold leading-snug text-gray-950 dark:text-white">{job.title}</p>
        <p className="mt-0.5 line-clamp-1 text-[10px] font-medium text-slate-600 dark:text-gray-400">{job.company}</p>
        <p className="mt-0.5 text-[9px] leading-snug text-slate-400">
          {job.source} · {job.postedLabel}
        </p>
      </div>

      <div className="home-hero-preview-job-card__tags mt-1.5 flex min-w-0 flex-col gap-0.5">
        {visibleSkills.map((skill) => (
          <span key={skill} className="home-hero-preview-job-card__tag w-full truncate">
            {skill}
          </span>
        ))}
        {extraSkills > 0 ? (
          <span className="home-hero-preview-job-card__tag home-hero-preview-job-card__tag--more w-fit">
            {heroCopy.jobsExtraSkills.replace('{{count}}', String(extraSkills))}
          </span>
        ) : null}
      </div>

      <p className="mt-1 shrink-0 text-[9px] leading-snug text-slate-400">{job.location}</p>

      <div className="home-hero-preview-job-card__footer mt-auto shrink-0">
        <p className="truncate text-[9px] text-slate-400">{job.experienceLevel}</p>
        <span className="home-hero-preview-job-card__arrow" aria-hidden>
          <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.25} />
        </span>
      </div>
    </button>
  );
}

function PreviewDrawer({ job, onClose }: { job: PreviewJob; onClose?: () => void }) {
  return (
    <motion.aside
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'spring', stiffness: 340, damping: 32 }}
      className="hfs-agent-preview__drawer absolute bottom-0 right-0 top-0 z-[70] flex w-[88%] min-w-[10rem] flex-col border-l border-gray-200 bg-white shadow-2xl dark:border-gray-800 dark:bg-[#0f0f11]"
    >
      {/* Header banner + avatar */}
      <div className="relative shrink-0 border-b border-gray-100 dark:border-gray-800">
        <div className="hfs-agent-preview__drawer-banner relative h-12 bg-gradient-to-r from-indigo-50 to-sky-50 dark:from-indigo-950/40 dark:to-sky-950/20">
          <div className="absolute right-2 top-2 flex items-center gap-1">
            <span className="flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
              <Bookmark className={`h-3 w-3 ${job.saved ? 'fill-indigo-500 text-indigo-500' : 'text-gray-400'}`} />
            </span>
            <button
              type="button"
              className="flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900"
              aria-label="Close"
              onClick={onClose}
            >
              <X className="h-3 w-3 text-gray-500" />
            </button>
          </div>
          <div className="absolute bottom-0 left-3 z-10 flex h-10 w-10 translate-y-1/2 items-center justify-center rounded-full border-2 border-white bg-indigo-100 text-sm font-bold text-indigo-700 shadow-md dark:border-gray-900 dark:bg-indigo-950/60 dark:text-indigo-300">
            {job.company.charAt(0)}
          </div>
        </div>

        <div className="px-3 pb-2.5 pt-6">
          <h3 className="text-[11px] font-bold leading-snug text-gray-950 dark:text-white">{job.title}</h3>
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
            <span className="text-[9px] text-gray-600 dark:text-gray-400">{job.company}</span>
            <span className="h-1 w-1 rounded-full bg-emerald-500" />
            <span className="rounded-full bg-sky-50 px-1.5 py-0.5 text-[7px] font-semibold text-sky-700 dark:bg-sky-950/40 dark:text-sky-300">
              {job.source}
            </span>
          </div>
          <p className="mt-1 flex items-center gap-1 text-[8px] text-gray-400">
            <Timer className="h-2.5 w-2.5" />
            {job.postedLabel}
          </p>
        </div>
      </div>

      {/* Scrollable body */}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 pb-2">
        {/* Match score + band */}
        <div className="mb-2.5 flex items-center gap-2.5 rounded-lg border border-emerald-100 bg-emerald-50/60 p-2 dark:border-emerald-900/40 dark:bg-emerald-950/20">
          <PreviewMatchRing score={job.score} ringId={`drawer-${job.id}`} size="md" highlight />
          <div className="min-w-0">
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[8px] font-bold text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300">
              <CheckCircle2 className="h-2.5 w-2.5" />
              Strong fit
            </span>
            <p className="mt-1 text-[8px] leading-snug text-gray-600 dark:text-gray-400">
              Based on your skills, experience &amp; location.
            </p>
          </div>
        </div>

        {/* Meta grid */}
        <div className="mb-2.5 grid grid-cols-2 gap-1">
          {[
            { icon: MapPin, label: job.location },
            { icon: Briefcase, label: job.jobType },
            { icon: DollarSign, label: job.salary },
            { icon: Timer, label: job.experienceLevel },
          ].map(({ icon: Icon, label }) => (
            <div key={label} className="hfs-agent-preview__meta-pill">
              <Icon className="h-2.5 w-2.5 shrink-0 text-gray-400" />
              <span className="truncate">{label}</span>
            </div>
          ))}
        </div>

        {/* Why you're a match */}
        <div className="mb-2.5 rounded-lg border border-indigo-100 bg-indigo-50/50 p-2 dark:border-indigo-900/40 dark:bg-indigo-950/20">
          <p className="mb-1 flex items-center gap-1 text-[8px] font-bold text-indigo-800 dark:text-indigo-300">
            <Lightbulb className="h-3 w-3" />
            Why you&apos;re a match
          </p>
          <p className="text-[8px] leading-relaxed text-gray-700 dark:text-gray-300">{job.whyMatched}</p>
        </div>

        {/* Skills you have */}
        {job.matchedSkills.length > 0 && (
          <div className="mb-2.5 rounded-lg border border-emerald-100/80 bg-emerald-50/40 p-2 dark:border-emerald-900/30">
            <p className="mb-1 text-[8px] font-bold uppercase tracking-wide text-emerald-800 dark:text-emerald-300">
              Skills you have
            </p>
            <ul className="space-y-0.5">
              {job.matchedSkills.map((s) => (
                <li key={s} className="flex items-start gap-1 text-[8px] text-gray-700 dark:text-gray-300">
                  <CheckCircle2 className="mt-0.5 h-2.5 w-2.5 shrink-0 text-emerald-600" />
                  {s}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Skill gaps */}
        {job.missingSkills.length > 0 && (
          <div className="mb-2.5 rounded-lg border border-gray-200 bg-gray-50/80 p-2 dark:border-gray-800 dark:bg-gray-900/40">
            <p className="mb-1 flex items-center gap-1 text-[8px] font-bold uppercase tracking-wide text-gray-600">
              <AlertCircle className="h-2.5 w-2.5" />
              Skill gaps
            </p>
            <ul className="space-y-0.5">
              {job.missingSkills.map((s) => (
                <li key={s} className="flex items-start gap-1 text-[8px] text-gray-600 dark:text-gray-400">
                  <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-gray-400" />
                  {s}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Bonus skills */}
        {job.bonusSkills.length > 0 && (
          <div className="mb-2.5 rounded-lg border border-sky-100 bg-sky-50/50 p-2 dark:border-sky-900/30">
            <p className="mb-1 text-[8px] font-bold uppercase tracking-wide text-sky-800 dark:text-sky-300">
              Bonus skills you bring
            </p>
            <div className="flex flex-wrap gap-0.5">
              {job.bonusSkills.map((s) => (
                <span key={s} className="hfs-agent-preview__skill-chip hfs-agent-preview__skill-chip--bonus">
                  {s}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Role summary */}
        <div className="mb-2 rounded-lg border border-gray-100 p-2 dark:border-gray-800">
          <p className="mb-1 text-[8px] font-bold uppercase tracking-wide text-gray-400">About the role</p>
          <p className="text-[8px] leading-relaxed text-gray-600 dark:text-gray-400">{job.description}</p>
        </div>

        {/* Next steps */}
        {job.actionItems.length > 0 && (
          <div className="mb-1 rounded-lg border border-gray-100 p-2 dark:border-gray-800">
            <p className="mb-1.5 text-[8px] font-bold uppercase tracking-wide text-gray-400">Next steps</p>
            <ol className="space-y-1">
              {job.actionItems.map((item, i) => (
                <li key={item} className="flex gap-1.5 text-[8px] text-gray-600 dark:text-gray-400">
                  <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-[7px] font-bold text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300">
                    {i + 1}
                  </span>
                  {item}
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>

      {/* Footer actions */}
      <div className="flex shrink-0 gap-1.5 border-t border-gray-100 p-2.5 dark:border-gray-800">
        <button type="button" className="hfs-agent-preview__btn hfs-agent-preview__btn--secondary">
          <FileText className="h-3 w-3" />
          Optimize
        </button>
        <button type="button" className="hfs-agent-preview__btn hfs-agent-preview__btn--primary">
          <Zap className="h-3 w-3" />
          Apply
        </button>
      </div>
    </motion.aside>
  );
}

function PreviewCursor({
  x,
  y,
  clicking,
  visible,
}: {
  x: number;
  y: number;
  clicking: boolean;
  visible: boolean;
}) {
  if (!visible) return null;

  return (
    <motion.div
      className="home-hero-preview-cursor pointer-events-none absolute z-[80]"
      initial={false}
      animate={{ left: x, top: y, scale: clicking ? 0.84 : 1, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 280, damping: 24, mass: 0.55 }}
    >
      <svg width="16" height="20" viewBox="0 0 18 22" fill="none" aria-hidden className="drop-shadow-sm">
        <path d="M1 1L1 16L5 12L8 20L11 19L8 11L14 10L1 1Z" fill="#0f172a" stroke="#ffffff" strokeWidth="1.25" />
      </svg>
      {clicking ? <span className="home-hero-preview-cursor-ripple" aria-hidden /> : null}
    </motion.div>
  );
}

const AgenticJobSearchFeaturePreview: React.FC = () => {
  const copy = useFeaturePreviewCopy();
  const hero = useHeroPreviewCopy();
  const previewJobs = useAgenticPreviewJobs();
  const firstJobId = previewJobs[0]?.id ?? '';
  const USER_QUERY = copy.agentSearchQuery;
  const rootRef = useRef<HTMLDivElement>(null);
  const jobTargetRef = useRef<HTMLButtonElement>(null);

  const [phase, setPhase] = useState<SimPhase>('typing');
  const [typedLen, setTypedLen] = useState(0);
  const [searchProgress, setSearchProgress] = useState(0);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [cursor, setCursor] = useState({ x: 24, y: 24, visible: false, clicking: false });

  const selectedJob = previewJobs.find((j) => j.id === selectedJobId) ?? previewJobs[0];
  const showJobs = phase === 'matches' || phase === 'cursor' || phase === 'drawer';
  const showTypingCursor = phase === 'typing' && typedLen < USER_QUERY.length;

  const getJobTargetCenter = useCallback(() => {
    const root = rootRef.current;
    const el = jobTargetRef.current;
    if (!root || !el) return null;
    const rootRect = root.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    return {
      x: elRect.left - rootRect.left + elRect.width * 0.55,
      y: elRect.top - rootRect.top + elRect.height * 0.5,
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const timers: number[] = [];
    const sleep = (ms: number) =>
      new Promise<void>((resolve) => {
        timers.push(window.setTimeout(resolve, ms));
      });

    const reset = () => {
      setPhase('typing');
      setTypedLen(0);
      setSearchProgress(0);
      setSelectedJobId(null);
      setDrawerOpen(false);
      setCursor({ x: 24, y: 24, visible: false, clicking: false });
    };

    const runScript = async () => {
      await sleep(600);
      while (!cancelled) {
        reset();
        await sleep(400);

        for (let i = 0; i <= USER_QUERY.length; i += 1) {
          if (cancelled) return;
          setTypedLen(i);
          await sleep(TYPING_MS);
        }
        await sleep(650);

        setPhase('searching');
        for (let p = 0; p <= 100; p += 4) {
          if (cancelled) return;
          setSearchProgress(p);
          await sleep(80);
        }
        await sleep(500);

        setPhase('matches');
        await sleep(2600);

        setPhase('cursor');
        await sleep(200);
        const start = getJobTargetCenter();
        if (start) {
          setCursor({ ...start, visible: true, clicking: false });
          await sleep(700);
          setCursor((c) => ({ ...c, clicking: true }));
          await sleep(140);
          setSelectedJobId(firstJobId);
          setCursor((c) => ({ ...c, clicking: false }));
          await sleep(300);
        }

        setPhase('drawer');
        setDrawerOpen(true);
        await sleep(4200);

        setDrawerOpen(false);
        setCursor((c) => ({ ...c, visible: false }));
        await sleep(900);
      }
    };

    void runScript();

    return () => {
      cancelled = true;
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, [getJobTargetCenter, firstJobId, USER_QUERY.length]);

  return (
    <div
      ref={rootRef}
      className="hfs-agent-preview relative flex h-full min-h-0 flex-col overflow-hidden bg-white font-sans antialiased dark:bg-[#0f0f11]"
    >
      <PreviewCursor {...cursor} />

      <div className="grid h-full min-h-0 flex-1 grid-cols-1 overflow-hidden md:grid-cols-12">
        <div className="flex min-h-0 flex-col border-b border-gray-200 md:col-span-5 md:border-b-0 md:border-r dark:border-gray-800">
          <div className="flex shrink-0 items-center gap-2 border-b border-gray-100 px-3 py-2 dark:border-gray-800">
            <Sparkles className="h-3.5 w-3.5 shrink-0 text-indigo-600" />
            <span className="text-[11px] font-semibold text-gray-900 dark:text-white">{copy.agentBrand}</span>
            <span className="ml-auto h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-emerald-500" />
          </div>

          <div className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-hidden p-3">
            <div className="shrink-0">
              <span className="text-[9px] font-bold uppercase tracking-wider text-gray-400">{copy.you}</span>
              <div className="mt-1 min-h-[52px] rounded-xl border border-gray-100 bg-gray-50 px-3 py-2.5 text-[10px] font-medium leading-relaxed text-gray-900 dark:border-gray-800 dark:bg-gray-900/60 dark:text-gray-100">
                {USER_QUERY.slice(0, typedLen)}
                {showTypingCursor ? (
                  <span className="ml-0.5 inline-block h-3.5 w-[2px] animate-pulse bg-gray-900 align-middle dark:bg-white" />
                ) : null}
              </div>
            </div>

            <AnimatePresence mode="wait">
              {phase === 'searching' && (
                <motion.div
                  key="searching"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  className="shrink-0 space-y-1.5"
                >
                  <span className="text-[9px] font-bold uppercase tracking-wider text-indigo-600">
                    {hero.agentProcessingLabel}
                  </span>
                  <div className="rounded-xl border border-indigo-100/60 bg-gradient-to-br from-indigo-50/60 to-sky-50/30 p-2.5 dark:border-indigo-900/40">
                    <p className="flex items-center gap-1.5 text-[10px] font-bold text-indigo-950">
                      <Search className="h-3 w-3 shrink-0" />
                      {hero.stepSearch}
                    </p>
                    <p className="mt-1 text-[9px] leading-snug text-gray-600 dark:text-gray-400">
                      {hero.agentAnalyzingBody}
                    </p>
                    <div className="mt-2 h-1 overflow-hidden rounded-full bg-indigo-100/80">
                      <motion.div
                        className="h-full rounded-full bg-indigo-600"
                        animate={{ width: `${searchProgress}%` }}
                        transition={{ duration: 0.08, ease: 'linear' }}
                      />
                    </div>
                  </div>
                </motion.div>
              )}

              {showJobs && (
                <motion.div
                  key="reply"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="shrink-0 space-y-1.5"
                >
                  <span className="text-[9px] font-bold uppercase tracking-wider text-indigo-600">
                    {hero.agentProcessingLabel}
                  </span>
                  <div className="rounded-xl border border-emerald-100/60 bg-gradient-to-br from-emerald-50/50 to-indigo-50/30 p-2.5">
                    <p className="text-[10px] font-bold text-gray-950">{hero.agentReplyTitle}</p>
                    <p className="mt-1 text-[9px] leading-snug text-gray-600">
                      {copy.rankedClickRole}
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="mt-auto shrink-0 rounded-xl border border-dashed border-gray-200 px-2.5 py-2 text-[8px] text-gray-400 dark:border-gray-700">
              {hero.promptHint}
            </div>
          </div>
        </div>

        <div className="relative flex min-h-0 flex-1 flex-col bg-[#fafafa] md:col-span-7 dark:bg-[#111113]">
          <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-3 py-2 dark:border-gray-800">
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
              {hero.matchFeed}
            </span>
            <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9px] font-medium text-emerald-700">
              {showJobs ? copy.matchesCount(previewJobs.length) : copy.scanning}
            </span>
          </div>

          <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden p-2">
            <p className="mb-1.5 shrink-0 text-[9px] font-medium text-slate-500">
              {showJobs ? copy.recommendedForProfile : copy.waitingAgentResults}
            </p>

            <div className="grid min-h-0 flex-1 grid-cols-2 grid-rows-2 gap-1.5">
              {previewJobs.map((job, index) => {
                const isTarget = index === 0;
                const isSelected = selectedJobId === job.id;
                const dimmed = showJobs && drawerOpen && !isSelected;

                return (
                  <motion.div
                    key={job.id}
                    initial={{ opacity: 0.2, y: 6 }}
                    animate={{
                      opacity: showJobs ? (dimmed ? 0.45 : 1) : 0.18,
                      y: 0,
                      scale: isSelected ? 1.02 : 1,
                    }}
                    transition={{ delay: showJobs ? index * 0.08 : 0 }}
                    className="min-h-0"
                  >
                    <PreviewJobCard
                      job={job}
                      selected={isSelected}
                      highlightRing={showJobs && isTarget}
                      cardRef={isTarget ? jobTargetRef : undefined}
                    />
                  </motion.div>
                );
              })}
            </div>

            <AnimatePresence>
              {drawerOpen && (
                <>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 z-[60] bg-slate-900/20"
                  />
                  <PreviewDrawer job={selectedJob} />
                </>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgenticJobSearchFeaturePreview;
